import {
  Grid,
  Typography,
  Divider,
  Box,
  Chip,
  Paper,
  Button,
  TablePagination,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useIntl } from "react-intl";
import { useMemo, useState } from "react";
import { Spinner } from "react-bootstrap";
import ModalComponent from "components/Modal";
import EditIcon from "@mui/icons-material/Edit";
import { numericFormatter } from "react-number-format";
import FilePresentIcon from "@mui/icons-material/FilePresent";

interface Proveedor {
  id: number;
  usuario: string;
  passwd: string;
  codigo: string;
  razon_social: string;
  nombre_comercial: string;
  direccion: string;
  contacto: string;
  giro: string;
  rfc: string;
  importe_credito: number;
  dias_credito: number;
  tiempo_entrega: number;
  correo: string;
  telefono: string;
  fecha_registro: Date;
  id_usuario: number;
  estatus: number;
  created_at: Date;
  updated_at: Date;
}

interface DetalleCanjeProps {
  verProveedor?: Proveedor | null;
  procesandoProveedor?: boolean;
  usuarioArjion?: string | null;
  idUsuario?: number;
  jefeInmediato: number;
  setVerEditarProveedor: React.Dispatch<React.SetStateAction<any>>;
  handleisAlertOpenCrearProveedor: () => void;
  handleisAlertOpenDetalleDocumentos: () => void;
}

const DetallesProveedor = ({
  verProveedor,
  procesandoProveedor,
  usuarioArjion,
  idUsuario,
  jefeInmediato,
  setVerEditarProveedor,
  handleisAlertOpenCrearProveedor,
  handleisAlertOpenDetalleDocumentos,
}: DetalleCanjeProps) => {
  if (!verProveedor) return null;
  const intl = useIntl();

  const [pageProduct, setPageProduct] = useState(0);
  const [rowsProductsPerPage, setRowsProductsPerPage] = useState(3);

  const privilegiosToRol: Record<string, string> = {
    "froebel.ivan": "admin",
    "torres.tannya": "daf",
    "salazar.mario": "dg",
    "hernandez.lucero": "compras",
    "delarosa.brenda": "compras",
  };

  const tipoUsuarioArjion = privilegiosToRol[usuarioArjion as string] ?? "";

  const getEstadoColor = (
    estatus_requisicion: number
  ): "info" | "warning" | "primary" | "default" | "error" | "success" => {
    switch (estatus_requisicion) {
      case 1:
        return "info"; // Creada
      case 2:
        return "warning"; // Autorizada Jefe Inmediato
      case 3:
        return "primary"; // Autorizada Director
      case 4:
        return "primary"; // Autorizada DAF
      case 5:
        return "primary"; // Autorizada DG
      case 6:
        return "success"; // Atendida
      case 7:
        return "error"; // Rechazada
      default:
        return "default";
    }
  };

  const estadosMap: { [key: number]: string } = {
    1: "Creada",
    2: "Autorizada Jefe Inmediato",
    3: "Autorizada Director de Area",
    4: "Autorizada DAF",
    5: "Autorizada DG",
    6: "Atendida",
    7: "Rechazada",
  };

  // Función helper para traducir estados
  const traducirEstado = (estado: number | null): string => {
    if (estado === null || estado === undefined) return "SIN ESTADO";
    return estadosMap[estado] ?? "SIN ESTADO";
  };

  return (
    <Box sx={{ px: 0, pb: 0, mt: 0 }}>
      <Grid container spacing={2}>
        {/* Header */}
        <Grid item xs={12}>
          <Box sx={{ textAlign: "center", mb: 0 }}>
            <Typography variant="h4" color="primary" gutterBottom sx={{ color: "#004c93" }}>
              {verProveedor.nombre_comercial}
            </Typography>
          </Box>
        </Grid>
        {/* Info general */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 2 }}>
            <Typography variant="h6" color="primary" gutterBottom sx={{ color: "#004c93" }}>
              Información principal
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Código
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {verProveedor.codigo}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Nombre comercial
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {verProveedor.nombre_comercial}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  RFC
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {verProveedor.rfc}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  Razón social
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {verProveedor.razon_social}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  Fecha de registro
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {new Date(verProveedor.created_at).toLocaleDateString("es-MX")}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        {/* Info personal */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 2 }}>
            <Typography variant="h6" color="primary" gutterBottom sx={{ color: "#004c93" }}>
              Información de contacto
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Contacto
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {verProveedor.contacto}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Correo
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {verProveedor.correo}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Teléfono
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {verProveedor.telefono}
                </Typography>
              </Grid>
              <Grid item xs={12} md={12}>
                <Typography variant="body2" color="text.secondary">
                  Dirección
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {verProveedor.direccion}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        {/* Info credito */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 2 }}>
            <Typography variant="h6" color="primary" gutterBottom sx={{ color: "#004c93" }}>
              Información de crédito
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Importe de crédito
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {numericFormatter(verProveedor.importe_credito + "", {
                    thousandSeparator: ",",
                    decimalScale: 2,
                    fixedDecimalScale: false,
                    prefix: "$",
                  })}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Días de crédito
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {verProveedor.dias_credito}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Tiempo de entrega
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {verProveedor.tiempo_entrega}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};
export default DetallesProveedor;
